import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { User } from '../types';

export type FieldUser = User;

interface FieldAuthContextType {
  user: FieldUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  forcePasswordChange: { token: string; required: boolean } | null;
  login: (phoneOrEmail: string, password: string) => Promise<FieldUser>;
  changePin: (newPassword: string) => Promise<void>;
  logout: () => void;
  clearForcePasswordChange: () => void;
}

const FieldAuthContext = createContext<FieldAuthContextType | undefined>(undefined);

export const useFieldAuth = () => {
  const context = useContext(FieldAuthContext);
  if (!context) {
    throw new Error('useFieldAuth must be used within FieldAuthProvider');
  }
  return context;
};

export const FieldAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FieldUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState<{ token: string; required: boolean } | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('field_uzman_user');
    const token = localStorage.getItem('field_uzman_token');

    if (storedUser && token) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
      } catch (e) {
        localStorage.removeItem('field_uzman_user');
        localStorage.removeItem('field_uzman_token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (phoneOrEmail: string, password: string): Promise<FieldUser> => {
    try {
      setLoading(true);
      const res = await authApi.mobileLogin({ phoneOrEmail, password });

      if (res.success && res.token && res.user) {
        setUser(res.user);
        localStorage.setItem('field_uzman_user', JSON.stringify(res.user));
        localStorage.setItem('field_uzman_token', res.token);
        // Web token'ı da senkronize edelim ki web API'leri de çalışsın
        localStorage.setItem('uzman_token', res.token);
        localStorage.setItem('uzman_user', JSON.stringify(res.user));
        setForcePasswordChange(null);
        return res.user;
      }
      throw new Error(res.message || 'Giriş yapılamadı.');
    } catch (err: any) {
      if (err.response?.status === 403 && err.response?.data?.code === 'MUST_CHANGE_PASSWORD') {
        const tempToken = err.response.data.tempToken;
        setForcePasswordChange({
          token: tempToken,
          required: true
        });
        throw new Error('MUST_CHANGE_PASSWORD');
      }
      const message = err.response?.data?.message || err.message || 'Giriş başarısız.';
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const changePin = async (newPassword: string): Promise<void> => {
    if (!forcePasswordChange?.token) {
      throw new Error('Geçici oturum bulunamadı.');
    }
    await authApi.mobileChangePin({ newPassword }, forcePasswordChange.token);
    setForcePasswordChange(null);
  };

  const logout = () => {
    setUser(null);
    setForcePasswordChange(null);
    localStorage.removeItem('field_uzman_user');
    localStorage.removeItem('field_uzman_token');
    window.location.href = '/field/login';
  };

  const clearForcePasswordChange = () => {
    setForcePasswordChange(null);
  };

  return (
    <FieldAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        forcePasswordChange,
        login,
        changePin,
        logout,
        clearForcePasswordChange
      }}
    >
      {children}
    </FieldAuthContext.Provider>
  );
};
